// Shared bits for widget adapters: one async loader hook and an empty-state helper.
import { useEffect, useState } from 'react';
import type { OrbWidgetModel, WidgetCtx } from '../types';

/** Run `load` when deps change; returns null until it resolves (and on failure). Never throws. */
export function useLoad<T>(load: (() => Promise<T>) | null, deps: unknown[]): { value: T | null; loading: boolean } {
  const [value, setValue] = useState<T | null>(null);
  const [loading, setLoading] = useState(!!load);
  useEffect(() => {
    if (!load) {
      setValue(null);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    load()
      .then((v) => live && setValue(v))
      .catch((e) => {
        console.warn('[orb widget] load failed', e);
        if (live) setValue(null);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { value, loading };
}

/** "Open TOOL" secondary action. */
export const openAction = (c: WidgetCtx, toolId: string, name: string, opts?: Parameters<WidgetCtx['openTool']>[1]) => ({
  label: `Open ${name}`,
  run: () => c.openTool(toolId, opts),
});

/** A clean empty model: quiet, `summary` is the empty-state line, one Open action. */
export function emptyModel(base: Pick<OrbWidgetModel, 'id' | 'toolId' | 'orderFunction' | 'title' | 'deityName' | 'agentId'>, c: WidgetCtx, summary: string, primary?: OrbWidgetModel['primaryAction']): OrbWidgetModel {
  return {
    ...base,
    status: 'quiet',
    summary,
    empty: true,
    primaryAction: primary ?? openAction(c, base.toolId, base.deityName),
    secondaryActions: primary ? [openAction(c, base.toolId, base.deityName)] : [],
    signals: {},
  };
}
